import { queryClient } from '@/lib/query-client'
import { AxiosRequestConfig } from 'axios'
import { Decimal } from 'decimal.js'
import { z } from 'zod'
import { dynamicsApi, EntityLogicalName } from './client'
import { assertQuoteEditable } from './quote-editability'
import { QUERY_KEYS } from './query-keys'
import type { QuoteData } from './quotes'

export async function createRecord<D = string>(
  entityLogicalName: EntityLogicalName,
  body: Record<string, unknown>,
  options = '',
  bypassQuoteStateCheck = false
): Promise<D> {
  assertQuoteEditable(bypassQuoteStateCheck)

  const res = await dynamicsApi.post<D>(`/${entityLogicalName}${options}`, body, {
    headers: options
      ? { 'OData-MaxVersion': '4.0', 'OData-Version': '4.0', Prefer: 'return=representation' }
      : undefined,
  })
  if (!options) {
    return res.headers['odata-entityid'].split('(')[1].replace(')', '')
  }
  return res.data
}

export async function getRecord<T>(entityLogicalName: EntityLogicalName, id: string, options = '') {
  const res = await dynamicsApi.get<T>(`/${entityLogicalName}(${id})${options}`)
  return res.data
}

export async function getMultipleRecords<T>(
  entityLogicalName: EntityLogicalName,
  options = '',
  config?: AxiosRequestConfig
) {
  const res = await dynamicsApi.get<{ value: T[] }>(`/${entityLogicalName}${options}`, config)
  return res.data.value
}

export const globalOptionSetDefinitionSchema = z.object({
  Options: z.array(
    z.object({
      Value: z.number({ message: 'Option set value is missing.' }),
      Description: z.object({
        UserLocalizedLabel: z.object({
          Label: z.string({ message: 'Option set description label is missing.' }),
        }),
      }),
      Label: z.object({
        UserLocalizedLabel: z.object({
          Label: z.string({ message: 'Option set label is missing.' }),
        }),
      }),
    })
  ),
})
export type GlobalOptionSetDefinition = z.infer<typeof globalOptionSetDefinitionSchema>

export async function getGlobalOptionSetDefinition(
  optionSetName: 'dsy_ecosystem' | 'dsy_phase' | 'dsy_systemscopetags'
) {
  const res = await dynamicsApi.get<GlobalOptionSetDefinition>(`/GlobalOptionSetDefinitions(Name='${optionSetName}')`)
  return globalOptionSetDefinitionSchema.parse(res.data)
}

export async function updateRecord<T>(
  entityLogicalName: EntityLogicalName,
  id: string,
  body: T,
  bypassQuoteStateCheck = false
) {
  assertQuoteEditable(bypassQuoteStateCheck)
  const res = await dynamicsApi.patch(`/${entityLogicalName}(${id})`, body, {
    headers: { 'If-Match': '*' },
  })
  return res.data
}

export async function deleteRecord(entityLogicalName: EntityLogicalName, id: string, bypassQuoteStateCheck = false) {
  assertQuoteEditable(bypassQuoteStateCheck)
  const res = await dynamicsApi.delete(`/${entityLogicalName}(${id})`)
  return res.data
}

export function getTaxDecimal(
  price: number | Decimal | null,
  quantity: number | Decimal | null,
  previousQuote?: boolean
) {
  if (!price || (price instanceof Decimal && price.isZero())) {
    return new Decimal(0)
  }

  if (!quantity || (quantity instanceof Decimal && quantity.isZero())) {
    return new Decimal(0)
  }
  const quote = queryClient.getQueryData<QuoteData>(QUERY_KEYS.quote({ previousQuote }))

  const priceDecimal = new Decimal(price).toDecimalPlaces(2)
  const quantityDecimal = new Decimal(quantity)
  const taxRate = new Decimal(quote?.dsy_taxcode?.msdyn_taxrate ?? 0).div(100)

  return priceDecimal.mul(quantityDecimal).mul(taxRate).toDecimalPlaces(2)
}
